import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, resolveDynamicComponent as _resolveDynamicComponent, mergeProps as _mergeProps, withCtx as _withCtx, createBlock as _createBlock, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, withKeys as _withKeys, Teleport as _Teleport } from "vue"

const _hoisted_1 = { class: "mall-rail__stage" }
const _hoisted_2 = { class: "mall-rail__media" }
const _hoisted_3 = ["src"]
const _hoisted_4 = { class: "mall-rail__copy" }
const _hoisted_5 = { class: "mall-rail__name" }
const _hoisted_6 = { class: "mall-rail__price" }
const _hoisted_7 = {
  key: 0,
  class: "mall-rail__dots"
}
const _hoisted_8 = ["aria-label", "aria-current", "onClick"]
const _hoisted_9 = ["onKeydown"]
const _hoisted_10 = { class: "mall-rail-choice__panel" }
const _hoisted_11 = {
  id: "mall-rail-choice-title",
  class: "mall-rail-choice__title"
}
const _hoisted_12 = { class: "mall-rail-choice__actions" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = {
  key: 0,
  class: "mall-rail-choice__note"
}
const _hoisted_15 = ["value"]

import { onMounted, onUnmounted, ref } from 'vue'
import { fetchLiveProduct, fetchTopSalesProducts, type MallTopSalesProduct } from '@/api/mallProduct'
import { mallProductPath } from '@/constants/mall'
import {
  bannerNeedsRefresh,
  buildTopSalesBannerPrompt,
  resolveTopSalesBanner,
  type TopSalesBannerBrief,
} from '@/constants/mallTopSalesBanners'
import { useAdminAuth } from '@/composables/useAdminAuth'

interface RailSlide {
  code: string
  name: string
  price: number
  image: string
  thumbnail: string
  to: string
  design: number
  rank: number
  category: string
  summary: string
  description: string
  unit: string
  origin: string
  maker: string
}

const SLIDE_COUNT = 10
const ROTATE_MS = 6000


export default /*@__PURE__*/_defineComponent({
  __name: 'SectionMallBanner',
  setup(__props) {

const { isSuperAdmin, initAdminAuth } = useAdminAuth()
const rootRef = ref<HTMLElement | null>(null)
const slides = ref<RailSlide[]>([])
const index = ref(0)
const paused = ref(false)
const selected = ref<RailSlide | null>(null)
const copying = ref(false)
const copyNote = ref('')
const promptText = ref('')
let timer = 0

function won(value: number) {
  return `${value.toLocaleString('ko-KR')}원`
}

function slideBind(slide: RailSlide) {
  if (isSuperAdmin.value) return { type: 'button' }
  return { to: slide.to, target: '_blank', rel: 'noopener noreferrer' }
}

function toSlides(products: MallTopSalesProduct[]): RailSlide[] {
  return products.slice(0, SLIDE_COUNT).map((product, design) => ({
    code: product.code,
    name: product.name,
    price: product.price,
    image: resolveTopSalesBanner(product.code, product.thumbnail),
    thumbnail: product.thumbnail,
    to: mallProductPath(product.code),
    design,
    rank: product.rank || design + 1,
    category: product.category,
    summary: product.summary,
    description: product.description,
    unit: product.unit,
    origin: product.origin,
    maker: product.maker,
  }))
}

function onSlideClick(event: MouseEvent, slide: RailSlide) {
  if (!isSuperAdmin.value) return
  event.preventDefault()
  copyNote.value = ''
  promptText.value = ''
  selected.value = slide
  paused.value = true
}

function closeChoice() {
  selected.value = null
  copying.value = false
  const root = rootRef.value
  const hovering = Boolean(root?.matches(':hover'))
  const focused = Boolean(root && document.activeElement && root.contains(document.activeElement))
  paused.value = hovering || focused
}

function openProduct() {
  if (!selected.value) return
  window.open(selected.value.to, '_blank', 'noopener')
  closeChoice()
}

async function writeClipboard(text: string) {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text)
    return
  }
  const area = document.createElement('textarea')
  area.value = text
  area.setAttribute('readonly', 'true')
  document.body.appendChild(area)
  area.select()
  const copied = document.execCommand('copy')
  area.remove()
  if (!copied) throw new Error('copy failed')
}

async function copyPrompt() {
  if (copying.value || !slides.value.length) return
  copying.value = true
  copyNote.value = ''
  try {
    const briefs = await Promise.all(slides.value.map((slide) => toBrief(slide)))
    const text = buildTopSalesBannerPrompt(briefs)
    promptText.value = text
    await writeClipboard(text)
    const refreshCount = briefs.filter((brief) => bannerNeedsRefresh(brief.code, brief.thumbnail)).length
    copyNote.value = refreshCount
      ? `프롬프트를 복사했습니다. 새로 만들 배너는 ${refreshCount}개입니다.`
      : '프롬프트를 복사했습니다. 현재 배너는 대표 이미지와 맞습니다.'
  } catch {
    copyNote.value = '클립보드 복사에 실패했습니다. 아래 내용을 직접 복사해 주세요.'
  } finally {
    copying.value = false
  }
}

async function toBrief(slide: RailSlide): Promise<TopSalesBannerBrief> {
  const detail = await fetchLiveProduct(slide.code).catch(() => null)
  return {
    rank: slide.rank,
    code: slide.code,
    name: detail?.name || slide.name,
    category: detail?.category || slide.category,
    summary: detail?.summary || slide.summary,
    description: detail?.description || slide.description,
    price: detail?.price ?? slide.price,
    unit: detail?.unit || slide.unit,
    origin: detail?.origin || slide.origin,
    maker: detail?.maker || slide.maker,
    thumbnail: detail?.thumbnail || slide.thumbnail,
    specs: detail?.specs || [],
  }
}

function tick() {
  if (paused.value || document.hidden || slides.value.length < 2) return
  index.value = (index.value + 1) % slides.value.length
}

function start() {
  stop()
  timer = window.setInterval(tick, ROTATE_MS)
}

function stop() {
  if (!timer) return
  window.clearInterval(timer)
  timer = 0
}

function resume(event: MouseEvent | FocusEvent) {
  const root = event.currentTarget
  const next = event.relatedTarget
  if (!(root instanceof HTMLElement)) return
  if (next instanceof Node && root.contains(next)) return
  if (selected.value) return
  window.setTimeout(() => {
    if (selected.value) return
    if (root.matches(':hover') || root.contains(document.activeElement)) return
    paused.value = false
  }, 0)
}

function go(next: number) {
  index.value = next
  start()
}

onMounted(async () => {
  void initAdminAuth()
  try {
    slides.value = toSlides(await fetchTopSalesProducts())
    if (slides.value.length > 1) {
      index.value = Math.floor(Math.random() * slides.value.length)
    }
  } catch {
    slides.value = []
  }
  start()
})

onUnmounted(stop)

return (_ctx: any,_cache: any) => {
  return (slides.value.length)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        ref_key: "rootRef",
        ref: rootRef,
        class: "mall-rail",
        onMouseenter: _cache[0] || (_cache[0] = ($event: any) => (paused.value = true)),
        onMouseleave: resume,
        onFocusin: _cache[1] || (_cache[1] = ($event: any) => (paused.value = true)),
        onFocusout: resume
      }, [
        _createElementVNode("div", _hoisted_1, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slides.value, (slide, i) => {
            return (_openBlock(), _createBlock(_resolveDynamicComponent(_unref(isSuperAdmin) ? 'button' : 'router-link'), _mergeProps({
              key: slide.code
            }, { ref_for: true }, slideBind(slide), {
              class: ["mall-rail__card", [`is-d${slide.design}`, { 'is-on': i === index.value }]],
              "aria-hidden": i === index.value ? undefined : 'true',
              tabindex: i === index.value ? undefined : -1,
              onClick: ($event: any) => (onSlideClick($event, slide))
            }), {
              default: _withCtx(() => [
                _createElementVNode("span", _hoisted_2, [
                  _createElementVNode("img", {
                    src: slide.image,
                    alt: ""
                  }, null, 8, _hoisted_3)
                ]),
                _createElementVNode("span", _hoisted_4, [
                  _cache[2] || (_cache[2] = _createElementVNode("span", { class: "mall-rail__brand" }, "함우리몰", -1)),
                  _createElementVNode("span", _hoisted_5, _toDisplayString(slide.name), 1),
                  _createElementVNode("span", _hoisted_6, _toDisplayString(won(slide.price)), 1)
                ])
              ]),
              _: 2
            }, 1040, ["class", "aria-hidden", "tabindex", "onClick"]))
          }), 128))
        ]),
        (slides.value.length > 1)
          ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(slides.value, (slide, i) => {
                return (_openBlock(), _createElementBlock("button", {
                  key: slide.code,
                  type: "button",
                  class: _normalizeClass(["mall-rail__dot", { 'is-on': i === index.value }]),
                  "aria-label": `${slide.name} 보기`,
                  "aria-current": i === index.value ? 'true' : undefined,
                  onClick: ($event: any) => (go(i))
                }, null, 10, _hoisted_8))
              }), 128))
            ]))
          : _createCommentVNode("", true),
        (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
          (selected.value)
            ? (_openBlock(), _createElementBlock("div", {
                key: 0,
                class: "mall-rail-choice",
                role: "dialog",
                "aria-modal": "true",
                "aria-labelledby": "mall-rail-choice-title",
                onKeydown: _withKeys(_withModifiers(closeChoice, ["prevent"]), ["esc"])
              }, [
                _createElementVNode("button", {
                  type: "button",
                  class: "mall-rail-choice__catch",
                  "aria-label": "닫기",
                  onClick: closeChoice
                }),
                _createElementVNode("div", _hoisted_10, [
                  _cache[3] || (_cache[3] = _createElementVNode("p", { class: "mall-rail-choice__brand" }, "함우리몰 웹배너", -1)),
                  _createElementVNode("h2", _hoisted_11, _toDisplayString(selected.value.name), 1),
                  _createElementVNode("div", _hoisted_12, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "mall-rail-choice__btn",
                      onClick: openProduct
                    }, " 해당 상품으로 이동하기 "),
                    _createElementVNode("button", {
                      type: "button",
                      class: "mall-rail-choice__btn mall-rail-choice__btn--prompt",
                      disabled: copying.value,
                      onClick: copyPrompt
                    }, _toDisplayString(copying.value ? '프롬프트를 작성하는 중' : '배너 상품 선정하여 이미지 새로 만드는 프롬프트 작성하기'), 9, _hoisted_13)
                  ]),
                  (copyNote.value)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_14, _toDisplayString(copyNote.value), 1))
                    : _createCommentVNode("", true),
                  (promptText.value)
                    ? (_openBlock(), _createElementBlock("textarea", {
                        key: 1,
                        class: "mall-rail-choice__prompt",
                        readonly: "",
                        value: promptText.value
                      }, null, 8, _hoisted_15))
                    : _createCommentVNode("", true)
                ])
              ], 40, _hoisted_9))
            : _createCommentVNode("", true)
        ]))
      ], 544))
    : _createCommentVNode("", true)
}
}

})